import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { useKeyListener } from '@/hooks/useKeyListener';
import { GestureButton } from '../GestureButton';

const styles = StyleSheet.create({
  container: {
    borderRadius: 5,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffff',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.23,
    shadowRadius: 2.62,

    elevation: 4,
  },
  text: {
    color: '#3a3a3a',
    textAlign: 'center',
    verticalAlign: 'auto',
  },
  wrongBg: {
    backgroundColor: colors.red,
  },
  lightColor: {
    color: colors.lightGrey,
  },
  correctBg: {
    backgroundColor: colors.primary,
  },
  hidden: {
    opacity: 0,
  },
});

export function ClickableLetterSquare({
  currentOrder,
  order,
  letter,
  extraStyle,
  id,
  onCorrect,
  onWrong,
  isHidden,
  onWrongParam,
  marginLeft,
  size,
  wrongBoxChosen,
}) {
  const [isWrong, setIsWrong] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const responsiveStylesForLetter = StyleSheet.create({
    choiceWrapper: {
      paddingHorizontal: marginLeft,
      paddingVertical: marginLeft,
      marginLeft: marginLeft,
      marginBottom: useResponsiveHeight(1),
      width: size,
      height: size,
    },
    choiceText: {
      fontSize: size / 1.35,
    },
  });

  const onWrongClick = () => {
    setIsWrong(true);
    setTimeout(() => {
      setIsWrong(false);
    }, 500);
    onWrong(onWrongParam);
  };

  const onCorrectClick = () => {
    setIsCorrect(true);
    setShowHint(false);
    onCorrect(id, currentOrder, letter);
  };

  const onClick = () => {
    // Prevent double clicks by checking if we're already processing
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
    }, 200);
    if (isHidden) {
      return;
    }
    if (order === -1 || !order.includes(currentOrder)) {
      return onWrongClick();
    }
    if (order.includes(currentOrder)) {
      onCorrectClick();
    }
  };

  useKeyListener({
    correct: order?.includes?.(currentOrder) || false,
    letter,
    onCorrect: onCorrectClick,
    onWrong: onWrongClick,
    order: currentOrder,
  });

  useEffect(() => {
    if (wrongBoxChosen >= 2) {
      if (order === -1) {
        return;
      }
      if (order.includes(currentOrder)) {
        setShowHint(true);
      }
    }
  }, [wrongBoxChosen, currentOrder]);

  if (isHidden) {
    return (
      <View
        style={[
          styles.container,
          extraStyle,
          styles.hidden,
          responsiveStylesForLetter.choiceWrapper,
        ]}
      >
        <Text
          className="font-abz"
          style={[
            styles.text,
            (isWrong || isCorrect) && styles.lightColor,
            responsiveStylesForLetter.choiceText,
          ]}
        >
          {letter}
        </Text>
      </View>
    );
  }

  return (
    <GestureButton onPress={onClick}>
      <View
        style={[
          styles.container,
          extraStyle,
          isWrong && styles.wrongBg,
          (showHint || isCorrect) && styles.correctBg,
          responsiveStylesForLetter.choiceWrapper,
        ]}
      >
        <Text
          className="font-abz"
          style={[
            styles.text,
            (showHint || isWrong || isCorrect) && styles.lightColor,
            responsiveStylesForLetter.choiceText,
          ]}
        >
          {letter}
        </Text>
      </View>
    </GestureButton>
  );
}
